import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../store/store';
import { Order, OrderStatus } from '../../types';
import { format } from 'date-fns';
import { bg } from 'date-fns/locale';
import { useSettings } from '../../contexts/SettingsContext';
import { formatPrice as formatPriceForSettings } from '../../utils/currency';

const OrderDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  // const { orders } = useSelector((state: RootState) => state.orders);
  const [order, setOrder] = useState<Order | null>(null);
  const { currencySettings } = useSettings();

  useEffect(() => {
    if (id) {
      // Always fetch fresh data from API for detailed view
      const fetchOrder = async () => {
        try {
          const response = await fetch(`/api/v1/orders/${id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          });
          if (response.ok) {
            const orderData = await response.json();
            console.log('Order data from API:', orderData); // Debug
            setOrder(orderData.order || orderData); // Handle both formats
          } else {
            console.error('Failed to fetch order, status:', response.status);
          }
        } catch (error) {
          console.error('Error fetching order:', error);
        }
      };
      fetchOrder();
    }
  }, [id]);

  const formatPrice = (price: number | undefined | null) => formatPriceForSettings(price ?? 0, currencySettings);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'd MMM, yyyy HH:mm', { locale: bg });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getStatusLabel = (status: OrderStatus) => {
    return t(`user.orders.statuses.${status.toLowerCase()}`);
  };

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case 'PENDING': return 'warning';
      case 'CONFIRMED': return 'info';
      case 'PROCESSING': return 'primary';
      case 'SHIPPED': return 'secondary';
      case 'DELIVERED': return 'success';
      case 'CANCELLED': return 'error';
      case 'RETURNED': return 'default';
      default: return 'default';
    }
  };

  if (!order) {
    return (
      <Box>
        <Typography variant="h5">{t('user.orders.orderNotFound')}</Typography>
        <Button onClick={() => navigate('/orders')} sx={{ mt: 2 }}>
          {t('user.orders.backToOrders')}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="/orders"
          onClick={(e) => {
            e.preventDefault();
            navigate('/orders');
          }}
        >
          {t('user.orders.title')}
        </Link>
        <Typography color="text.primary">
          {t('user.orders.orderNumber')} #{order.orderNumber}
        </Typography>
      </Breadcrumbs>

      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/orders')}
        >
          {t('user.orders.backToOrders')}
        </Button>
        <Typography variant="h4" component="h1" fontWeight="bold">
          {t('user.orders.orderDetails')}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('user.orders.orderInformation')}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">{t('user.orders.orderNumber')}</Typography>
                  <Typography variant="h6">{order.orderNumber}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">{t('user.orders.orderDate')}</Typography>
                  <Typography variant="h6">{formatDate(order.createdAt!)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">{t('user.orders.status')}</Typography>
                  <Chip
                    label={getStatusLabel(order.status)}
                    color={getStatusColor(order.status) as any}
                    sx={{ mt: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="textSecondary">{t('user.orders.paymentMethod')}</Typography>
                  <Typography variant="h6">{order.paymentMethod?.replace('_', ' ') || 'N/A'}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('user.orders.orderItems')}</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('user.orders.product')}</TableCell>
                      <TableCell align="center">{t('user.orders.quantity')}</TableCell>
                      <TableCell align="right">{t('user.orders.unitPrice')}</TableCell>
                      <TableCell align="right">{t('user.orders.total')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(order.items || []).map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={2}>
                            <Box
                              component="img"
                              sx={{
                                height: 40,
                                width: 40,
                                borderRadius: 1,
                                objectFit: 'cover',
                              }}
                              src={item.product.imageUrl || '/placeholder-book.svg'}
                              alt={item.product.title}
                              onError={(e: any) => {
                                e.target.src = '/placeholder-book.svg';
                              }}
                            />
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                {item.product.title}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {item.product.author}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="right">{formatPrice(item.price)}</TableCell>
                        <TableCell align="right">{formatPrice(item.totalPrice)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body1">Междинна сума</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1">
                          {formatPrice(
                            (order.items || []).reduce((sum, item) => sum + (item.totalPrice || 0), 0)
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="body1">Доставка</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body1">
                          {order.shippingCost === null
                            ? 'В зависимост от тарифата на куриера'
                            : order.shippingCost === 0
                            ? 'Безплатна доставка!'
                            : formatPrice(order.shippingCost)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={3}>
                        <Typography variant="h6">{t('user.orders.total')}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="h6">{formatPrice(order.totalAmount)}</Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('user.orders.shippingAddress')}</Typography>
              <Typography variant="body2">
                {(() => {
                  const addr = order.shippingAddress as any;
                  if (!addr) return t('user.orders.noAddressProvided');
                  
                  const street = addr.address_line_1 || addr.address || 'N/A';
                  const city = addr.city || 'N/A';
                  const postalCode = addr.postal_code || addr.postalCode || 'N/A';
                  const country = addr.country || 'N/A';
                  
                  return (
                    <>
                      {street}<br />
                      {city} {postalCode}<br />
                      {country}
                    </>
                  );
                })()}
              </Typography>
            </CardContent>
          </Card>

          {order.notes && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>{t('user.orders.orderNotes')}</Typography>
                <Typography variant="body2">
                  {order.notes}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderDetailPage;
